/*
 * The Trust Fabric's model and drawing; TrustFabric.js is the screen.
 *
 * buildFabric turns what GET /api/v1/fabric answers (server/pkg/fabric) into
 * a tree of things, each opening into its parts: the customer's key, its
 * agents, its boxes and the gateways that countersign them; a box as the path
 * a change takes (who may ask, the guard, the trial, the logbook, its
 * gateway); a stage's rows; a row's signed fact. layoutFabric sizes each thing
 * from its content at its own reading size and places its inside within it,
 * scaled down, so zooming into a card shows what it holds at reading size and
 * nothing rewraps on the way. Everything drawn is SVG made here; the screen
 * measures, zooms and fetches.
 *
 * Only what the route sends is drawn, and it sends only what a signed fact
 * backs: every node and line carries its facts, the boxes nothing backs yet
 * are named in a line of their own, and every number is the route's.
 * Sentences are the server's where it gives them (a box's state, a page),
 * else made here from its fields.
 */
import React from "react";

const h = React.createElement;

/* ── Words and numbers ─────────────────────────────────────────────────── */
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const pad = (x) => String(x).padStart(2, "0");
const ms = (iso) => Date.parse(iso);
export const hm = (iso) => { const d = new Date(iso); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
export const hms = (iso) => `${hm(iso)}:${pad(new Date(iso).getSeconds())}`;
const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const on = (iso, now, clock) => {
  const d = new Date(iso), k = Math.round((midnight(new Date(now)) - midnight(d)) / 864e5);
  return k === 0 ? clock(iso) : k === 1 ? `yesterday ${clock(iso)}` : `${d.getDate()} ${MON[d.getMonth()]}, ${clock(iso)}`;
};
export const at = (iso, now) => on(iso, now, hm);
export const atS = (iso, now) => on(iso, now, hms);
export const asOf = (iso) => { const d = new Date(iso); return `${hm(iso)} on ${DAY[d.getDay()]} ${d.getDate()} ${MONTH[d.getMonth()]}`; };
export const n = (x) => Number(x).toLocaleString("en-GB");
export const list = (xs) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
const plural = (k, a, b) => `${k} ${k === 1 ? a : b}`;
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
// Only a sentence that starts with a word of ours is capitalised: a box, a
// gateway or a person keeps the name it was given.
const capArticle = (s) => s.replace(/^(the|a|an|your) /, (m) => cap(m));
const stop = (s) => { const t = String(s || "").trim(); return !t || /[.!?]$/.test(t) ? t : `${t}.`; };
export const mmss = (s) => { const t = Math.max(0, Math.round(s)); return `${Math.floor(t / 60)} min ${t % 60} s`; };
const untilW = (u) => (u ? ` until ${hm(u)}` : "");
// A digest as a person compares one: its first twelve characters, in fours.
const fp = (k) => String(k || "").replace(/^sha256:/, "").slice(0, 12).replace(/(.{4})(?=.)/g, "$1 ");

const REACH = ["reads only", "one kind of traffic", "the whole box", "the site’s connection", "more than one site"];
const reachOf = (r) => { const k = Number(String(r ?? "").replace(/^b/i, "")); return Number.isInteger(k) && k >= 0 && k < REACH.length ? k : 0; };
export const reachWords = (r) => REACH[reachOf(r)];

/* The box's eight checks, in the order it asks them, under the three
   questions they answer. */
export const CHECKS = ["", "Who is asking", "The clock", "Never seen before", "Meant for this box, in time", "On the menu", "The key card", "Your rules", "Allowance and breaker"];
const question = (k) => (k <= 4 ? "Who are you?" : k <= 7 ? "May you do this, here, now?" : "Has it done too much?");

/* An agent as a sentence names it: Weavian's own by what it is, the others by
   their names. */
const theAgent = (a) => (a.kind === "controller" ? `the ${lowerFirst(a.name)}` : a.name);

/* Who signed or asked, from the principal a fact names (authority.Name). */
export function signerWords(s) {
  const p = s && typeof s === "object" ? s.principal || "" : String(s || "");
  if (p === "spiffe://weavian.com/root") return "Weavian’s root key";
  const m = /^spiffe:\/\/[^/]+\/[^/]+\/([a-z]+)\/([^/]+)(?:\/agent\/([^/]+))?$/.exec(p);
  if (!m) return p;
  const [, kind, id, agent] = m;
  if (agent) return { fleet: "the fleet agent, through Weavian’s console", records: "the logbook copier, through Weavian’s console" }[agent] || `${agent} on ${id}`;
  if (kind === "agent") return id.startsWith("ada.") ? `Ada on ${id.slice(4)}` : id;
  return { node: `the box ${id}`, anchor: `${id}, a Weavian gateway`, controller: "Weavian’s console", authority: "your key", person: id, support: "Weavian support", outside: "an outside agent" }[kind] || id;
}

/* A fact cited by name and digest: name#digest, or the route's object with
   its signer and time. */
export function refOf(x) {
  if (!x) return null;
  if (typeof x === "string") { const i = x.lastIndexOf("#"); return i < 0 ? { name: x, digest: "" } : { name: x.slice(0, i), digest: x.slice(i + 1) }; }
  return x.name ? x : null;
}
export const refKey = (r) => (r ? `${r.name}#${r.digest || ""}` : "");
const newest = (facts) => (facts || []).map(refOf).filter(Boolean).reduce((a, f) => (!a || ms(f.at) > ms(a.at) ? f : a), null);
const parts = (name) => String(name).split("/").slice(1).map(decodeURIComponent);
/* The endorsement, the approver list and the mandates are statements the
   controller holds beside the key; they do not open through /api/v1/facts. */
export const isHeld = (name) => { const [c, a] = parts(name); return (c === "weavian" && a === "endorsement") || a === "approvers" || a === "mandate"; };

/* What a fact is, from its name alone (server/pkg/facts kinds.go). */
export function factTitle(name) {
  const [c, a, b, k, s, t, u] = parts(name);
  if (a === "edge") return { record: `Page ${n(s)} of ${b}’s logbook`, checkpoint: `Seal ${n(s)} of ${b}’s logbook`, head: `${b}’s logbook as it last reported it`, pin: `A key ${b} trusts`, state: `${b}’s state`, reach: `What ${b} lets others reach` }[k] || name;
  if (c === "weavian") {
    if (a === "anchor" && k === "witness") return `${b}’s countersignature ${n(u)} of ${t}’s logbook`;
    if (a === "endorsement") return `Weavian’s endorsement of your key, number ${k}`;
    if (a === "catalogue") return `The menu, version ${b}`;
    if (a === "node" && k === "rotation") return `${b}’s change of key ${s}`;
    return name;
  }
  return { mandate: `Key card ${parts(name).slice(3).join("/")}`, rules: `Your rules, version ${b}`, approvers: `Your approvers, list ${b}`, approval: `A yes from ${b}`, autonomy: `Trust earned for ${k}`, freeze: `Freeze ${b}`, cosign: `${b}’s cosignature of ${k}, page ${s}` }[a] || name;
}

/* What a fact says, from its signed content, for the kinds a person meets
   here; a kind without words says nothing rather than guess. */
export function factSays(f, now) {
  const c = f.content || {}, who = signerWords(c.principal), page = `Page ${n(c.seq)} of ${c.unit}’s logbook`;
  switch (f.kind) {
    case "record":
      switch (c.entry_kind) {
        case "refusal": return `${page}: ${who} asked for “${c.capability || c.entry}”, and the box refused it at check ${c.check}, ${lowerFirst(CHECKS[c.check] || "")}${c.reason ? `: ${c.reason}` : ""}.`;
        case "change": return `${page}: ${who} changed “${c.entry}”${c.outcome && c.outcome !== "applied" ? `, and it was ${c.outcome}` : ""}.`;
        case "witness_received": return `${page}: ${who} countersigned seal ${n(c.checkpoint)}${c.expects ? ` and expects page ${n(c.expects)} next` : ""}.`;
        case "rules_loaded": return `${page}: the box now judges what it is asked by the rules ${fp(c.version)}.`;
        case "catalogue_loaded": return `${page}: the box now offers the menu ${fp(c.version)}.`;
        default: return `${page} records ${c.entry_kind ? `a ${c.entry_kind.replace(/_/g, " ")} entry` : "an entry"}${c.entry ? ` for “${c.entry}”` : ""}${c.principal ? `, from ${who}` : ""}.`;
      }
    case "head":
      return `${c.unit}’s logbook stood at page ${n(c.seq)}${c.at ? ` at ${at(c.at, now)}` : ""}${c.checkpoint ? `, its newest seal ${n(c.checkpoint)} over pages 1 to ${n(c.last)}` : ""}.`;
    case "checkpoint":
      return c.last ? `Seal ${n(c.seq ?? c.checkpoint)} of ${c.unit}’s logbook: one fingerprint over pages 1 to ${n(c.last)}, so no page before it can change unseen.` : "";
    case "witness":
      return `${c.anchor} saw ${c.unit}’s logbook and what crossed its lines${c.from && c.to ? ` from ${at(c.from, now)} to ${hm(c.to)}` : ""}${c.expects ? `, and expects page ${n(c.expects)} next` : ""}.`;
    case "rules":
      return `Your rules, version ${c.version}${c.mode ? `, ${c.mode}` : ""}.`;
    default:
      return "";
  }
}

const REL = {
  prev: "The page before", parent: "The key card it narrows", mandate: "Its key card", approval: "A yes", checkpoint: "The seal",
  record: "The page", witness: "The countersignature", catalogue: "The menu", rules: "Your rules", earned_from: "Earned from", statement: "The statement",
};
/* The rows of a signed fact as both the canvas and the sheet show it: what it
   says, who signed it, its name and fingerprint, and what it links to. got is
   the fact read from its envelope, with the digest the screen computed; a fact
   the controller holds beside the key shows what the route carries of it. */
export function factRows(got, now) {
  const f = got.fact, s = f.signer || {}, rows = [], says = factSays(f, now);
  if (says) rows.push({ label: "What it says", value: says });
  rows.push({ label: "Signed by", value: `${capArticle(signerWords(s.principal))}${s.agent ? `, for ${signerWords(s.agent)}` : ""}, key ${fp(s.key)}` });
  rows.push({ label: "Its name", value: f.name, mono: true });
  rows.push({ label: "Fingerprint", value: `#${got.digest.slice(0, 8)}, signed ${atS(f.time, now)}`, mono: true });
  if (f.links?.length) rows.push({ label: "Links to", links: f.links.map((l) => ({ text: `${REL[l.rel] || cap(l.rel)}: ${factTitle(l.name)}`, ref: `${l.name}#${l.digest}` })) });
  return rows;
}
export function heldRows(ref, now) {
  return [
    ref.signer && { label: "Signed by", value: capArticle(signerWords(ref.signer)) },
    { label: "Its name", value: ref.name, mono: true },
    ref.digest && { label: "Fingerprint", value: `#${ref.digest.slice(0, 8)}${ref.at ? `, signed ${atS(ref.at, now)}` : ""}`, mono: true },
    { label: "Where it is", value: "Held by the controller, beside your key. It does not open here." },
  ].filter(Boolean);
}
export const provWords = (p, now) => `Signed by ${signerWords(p.signer)} at ${at(p.at, now)}`;

/* ── The model: the route's answer as a tree of things ─────────────────── */
const PILL = {
  change: { ok: "Changed", no: "Not made", wait: "On trial" }, verdict: { ok: "Kept", no: "Undone" }, revert: "Put back", undo: "Undone", refusal: "Refused",
  witness_received: { ok: "Countersigned", no: "Does not follow on", idle: "Countersigned" }, rules_loaded: "Rules loaded", catalogue_loaded: "Menu loaded",
  rotation: "New key", pin: "Key trusted", freeze: "Frozen", breaker: "Breaker", call: "Asked",
};
/* A page's pill: its kind in a word or two, in the route's tone. */
const pageWords = (e) => { const p = PILL[e.kind]; return (typeof p === "object" ? p[e.tone] : p) || "Recorded"; };
const LINE = { up: ["ok", "working"], down: ["idle", "down"], off: ["idle", "off"], standby: ["idle", "standby"], unknown: ["idle", "not heard"] };
export const running = (t, now) => !!t && !t.ended && ms(t.decides) > ms(now);

export function buildFabric(data) {
  const now = data.as_of, NODES = new Map();
  const node = (id, kind, o, parent) => {
    const x = { id, kind, kids: [], ...o };
    NODES.set(id, x);
    if (parent) { x.parent = parent; parent.kids.push(x); }
    return x;
  };
  const agents = data.agents || [], gateways = data.gateways || [], key = data.key || null;
  // Every fact the route cites, so a reference by name#digest shows its signer and time.
  const cited = new Map();
  for (const f of [key?.facts, ...agents.map((a) => a.facts), ...(data.boxes || []).map((b) => b.facts), ...gateways.map((g) => g.facts), ...(data.edges || []).map((e) => e.facts)].flat()) if (f?.name) cited.set(refKey(f), f);
  const cite = (x) => { const r = refOf(x); return r && (cited.get(refKey(r)) || r); };
  const live = (m) => !m.until || ms(m.until) > ms(now);
  const holders = (unit) => agents.flatMap((a) => {
    const mm = (a.may || []).filter((m) => m.unit === unit && live(m) && reachOf(m.reach) > 0);
    if (!mm.length) return [];
    return [{ a, reach: Math.max(...mm.map((m) => reachOf(m.reach))), until: mm.reduce((u, m) => (ms(m.until) > ms(u) ? m.until : u), mm[0].until), mm }];
  });
  // Boxes in the order of the gateways carrying them, so each band sits over its gateways.
  const carrier = (unit) => gateways.find((g) => (g.carries || []).includes(unit));
  const boxes = [...(data.boxes || [])].sort((x, y) => {
    const gx = gateways.indexOf(carrier(x.unit)), gy = gateways.indexOf(carrier(y.unit));
    return (gx < 0 ? 1e9 : gx) - (gy < 0 ? 1e9 : gy);
  });
  const lineOf = (b, label) => (b.lines || []).find((l) => l.label === label || l.name === label);
  const root = node("root", "root", { label: data.customer?.name || data.customer?.id || "Your network", now });
  const factTile = (p, f) => node(`${p.id}/fact:${refKey(f)}`, "tile", { label: factTitle(f.name), title: factTitle(f.name), chip: ["idle", `Signed ${at(f.at, now)}`], body: `Signed by ${signerWords(f.signer)}.`, fact: f }, p);

  /* Your key, or the place it would be. */
  if (key) {
    const ap = key.approvers || [], one = ap.length === 1, byPart = (p) => (key.facts || []).find((f) => parts(f.name)[1] === p);
    const k = node("key", "key", {
      label: "Your key", title: "Your key", sub: "Signs every key card", facts: key.facts, prov: newest(key.facts),
      say: `Every key card starts at your key, fingerprint ${key.fingerprint}, and Weavian’s root key vouches that it is yours. ${!ap.length ? "Nobody can say yes yet." : one ? `${ap[0]} is the only person who can say yes, so no change beyond one site can be approved until a second person joins.` : `${list(ap)} can say yes.`}${key.rules ? ` Your rules are version ${key.rules.version}, ${key.rules.mode}.` : ""}`,
    }, root);
    node("key/fp", "tile", { label: "Fingerprint", title: `Fingerprint ${key.fingerprint}`, chip: ["ok", "Vouched for"], body: "Weavian’s root key vouches that this key is yours. Every key card starts here, and every box holds it.", fact: byPart("endorsement") }, k);
    node("key/approvers", "tile", {
      label: "Who can say yes", title: ap.length ? `Who can say yes: ${list(ap)}` : "Nobody can say yes yet",
      chip: one ? ["wait", "Only one on the list"] : ap.length ? ["ok", plural(ap.length, "person", "people")] : ["idle", "Nobody yet"],
      body: `Each approver signs with a passkey: one yes for a change to a site’s connection, two for anything beyond one site.${one ? ` Only ${ap[0]} is on the list, so no change beyond one site can be approved until a second person joins.` : ""}`,
      fact: byPart("approvers"),
    }, k);
    if (key.rules) node("key/rules", "tile", { label: `Your rules, version ${key.rules.version}`, title: `Your rules, version ${key.rules.version}, ${key.rules.mode}`, chip: ["ok", "In force"], body: "Every box judges every request against the version of your rules it holds. Your rules can only narrow Weavian’s.", fact: byPart("rules") }, k);
  } else {
    node("key", "nokey", { label: "No key yet", title: "No key of yours yet", sub: "Agents may only read", say: "No key of yours signs key cards yet, so agents may only read. A key card is how your key lets an agent change a box." }, root);
  }

  /* The agents, each with what it may do and what named it. */
  for (const a of agents) {
    const mm = (a.may || []).filter((m) => live(m) && reachOf(m.reach) > 0), units = [...new Set(mm.map((m) => m.unit))];
    const r = a.today?.refused || 0, today = a.today?.asked ? ` Today it asked ${plural(a.today.asked, "time", "times")}${r ? `, and ${r === 1 ? "once" : `${r} times`} a box refused it` : ""}.` : "";
    const sub = units.length ? `May change ${plural(units.length, "box", "boxes")}` : a.kind === "assistant" ? "Proposes, changes nothing" : "Reads only";
    const say = a.kind === "assistant" ? `${a.name} is the assistant on each box. It reads its own box and proposes changes, which a person applies from the console. It holds no key card and changes nothing itself.`
      : `${cap(theAgent(a))} acts only under key cards from your key.${units.length ? ` It holds key cards for ${list(units)}.` : " It holds no key card that lets it change anything now, so it may only read."}${today}`;
    const an = node(`agent:${a.id}`, "agent", { a, label: a.name, title: a.name, sub, quiet: !units.length, facts: a.facts, prov: newest(a.facts), say }, root);
    const seen = new Set();
    for (const [ref, group] of groupBy(mm, (m) => refKey(refOf(m.mandate)))) {
      const until = group[0].until, us = group.map((m) => m.unit), f = cite(group[0].mandate);
      seen.add(ref);
      node(`${an.id}/card:${ref}`, "tile", { label: factTitle(f?.name || "Key card"), title: `${cap(reachWords(group[0].reach))} at ${list(us)}`, chip: ["ok", until ? `In force until ${hm(until)}` : "In force"], body: `${cap(theAgent(a))} may change ${reachWords(group[0].reach)} at ${list(us)}${untilW(until)}, under this key card from your key.`, fact: f }, an);
    }
    if (a.kind === "assistant") node(`${an.id}/proposes`, "tile", { label: "Proposes", title: "Proposes, never changes", chip: ["idle", "No key card"], body: "A person applies a proposal from the console, under the person’s own name." }, an);
    (a.facts || []).filter((f) => f?.name && !seen.has(refKey(f))).forEach((f) => factTile(an, f));
  }

  /* The boxes, each as the path a change takes. */
  for (const b of boxes) {
    const H = holders(b.unit), rec = b.record, t = b.trial, w = b.witness, g = gateways.find((x) => x.id === (w?.gateway || b.gateway)) || carrier(b.unit);
    const tr = running(t, now), left = tr ? (ms(t.decides) - ms(now)) / 1000 : 0, flag = rec?.flags?.[0];
    const may = H.map((x) => `${theAgent(x.a)} may change it${untilW(x.until)}`);
    const s1 = !rec ? `${b.unit} has sent no pages of its logbook yet.`
      : rec.agrees === false ? `${b.unit}’s logbook does not agree with ours.${flag ? ` ${stop(flag.why)}` : ""}`
      : tr ? `At ${b.unit}, a change is on trial: ${mmss(left)} left at ${hm(now)}. ${stop(t.words)}`
      : b.online === false ? `${b.unit} has not reported since ${at(b.last_heard, now)}. Its logbook agreed with ours then, at page ${n(rec.seq)}.`
      : `${b.unit} is calm, and its logbook agrees with ours at page ${n(rec.seq)}.`;
    const bn = node(`box:${b.unit}`, "box", {
      b, gw: g, label: b.unit, title: b.unit, role: b.name && b.name !== b.unit ? b.name : "", facts: b.facts, prov: newest(b.facts),
      who: H.map((x) => `${x.a.name} may change ${reachWords(x.reach)}${untilW(x.until)}`),
      pill: [b.state?.tone || "idle", b.state?.words || (b.online === false ? "Not heard" : "Calm")],
      say: `${s1} ${may.length ? stop(cap(list(may))) : key ? "No agent may change it right now." : "Agents may only read here."}`,
    }, root);
    bn.lines = (b.lines || []).map((l) => {
      const name = l.label || l.name;
      if (tr && lineOf(b, t.line) === l) return { name, brief: "on trial", words: `${t.line_words ? `${t.line_words}, ` : ""}on trial`, tone: "wait" };
      if (b.online === false) return { name, brief: "", words: "not heard while the box is not", tone: "idle" };
      const [tone, words] = LINE[l.state] || ["idle", "not heard"];
      return { name, brief: l.state === "up" ? "" : words, words, tone };
    });

    const menu = node(`${bn.id}/menu`, "part", { label: "Who may ask", title: "Who may ask", stage: "menu", say: `Who may ask ${b.unit} for a change, and under which key cards. ${may.length ? stop(cap(list(may))) : "Right now agents may only read here."}` }, bn);
    menu.rows = agents.map((a) => {
      const x = H.find((y) => y.a === a);
      return x ? { line: `${a.name}: ${reachWords(x.reach)}${x.until ? `, until ${hm(x.until)}` : ""}`, tone: "ok" }
        : a.kind === "assistant" ? { line: `${a.name}: proposes, changes nothing`, said: `${a.name} proposes and changes nothing.`, tone: "idle" }
        : { line: `${a.name}: reads only`, said: `${cap(theAgent(a))} holds no key card that lets it change ${b.unit}, so it may only read.`, tone: "idle" };
    });
    for (const x of H) for (const [ref, group] of groupBy(x.mm, (m) => refKey(refOf(m.mandate)))) {
      const all = (x.a.may || []).filter((m) => refKey(refOf(m.mandate)) === ref).map((m) => m.unit), until = group[0].until, f = cite(group[0].mandate);
      node(`${menu.id}/card:${ref}`, "tile", { card: true, label: factTitle(f?.name || "Key card"), title: `${cap(reachWords(group[0].reach))} at ${list(all)}`, chip: ["ok", until ? `In force until ${hm(until)}` : "In force"], lines: [`${x.a.name} may change ${reachWords(group[0].reach)} here`, until ? `Until ${hm(until)}` : "Until it is revoked", key ? "Signed by your key" : "Signed through Weavian’s console"], body: `${cap(theAgent(x.a))} may change ${reachWords(group[0].reach)} at ${list(all)}${untilW(until)}.`, fact: f }, menu);
    }

    const gd = b.guard || {}, through = gd.today?.through || 0, stopped = gd.today?.stopped || 0;
    const today = through + stopped ? `Today ${through} through, ${stopped} stopped.` : "Nothing asked to change it today.";
    const guard = node(`${bn.id}/guard`, "part", { label: "Guard", title: "The guard", stage: "guard", through, stopped, say: `${b.unit} checks every request eight ways before anything changes, and stops at the first no. ${today}` }, bn);
    let reached = through + stopped;
    for (let k = 1; k <= 8; k++) {
      const s = Number(gd.by_check?.[k] || 0), passed = Math.max(0, reached - s);
      node(`${guard.id}/${k}`, "tile", {
        num: k, name: CHECKS[k], q: question(k), label: `Check ${k}`, title: `${k}. ${CHECKS[k]}`,
        chip: s ? ["no", passed ? `Passed ${passed}, stopped ${s}` : `Stopped ${s}`] : passed ? ["ok", `Passed ${passed}`] : ["idle", "Nothing today"],
        mark: s ? ["no", `✕ ${s} stopped`] : passed ? ["ok", "✓"] : ["idle", "not used today"],
        last: s ? `It stopped ${plural(s, "request", "requests")} today${passed ? ` and let ${passed} on` : ""}.` : passed ? `Every request that reached it today passed: ${passed}.` : "No request reached it today.",
      }, guard);
      reached = passed;
    }

    const trial = node(`${bn.id}/trial`, "part", { label: "Trial", title: "The trial", stage: "trial", t,
      say: !t ? `Nothing is on trial at ${b.unit}. A change an agent makes is tried here before it is kept.` : tr ? `${b.unit} is trying a change. ${stop(t.words)} If it is not better by ${hms(t.decides)}, the box puts it back by itself.${t.waits_for ? ` If it is, ${t.waits_for} confirms it.` : ""}` : `The last change tried at ${b.unit}, at ${at(t.asked, now)}. ${stop(t.words)} ${stop(t.outcome?.words)}`.trim() }, bn);
    if (t) {
      const f = t.facts || {}, o = t.outcome || { tone: tr ? "wait" : "idle", words: tr ? "On trial" : "Ended" }, took = (ms(t.decides) - ms(t.applied)) / 1000;
      const step = (k, o2) => node(`${trial.id}/${k}`, "tile", { step: k, label: o2.title, ...o2 }, trial);
      step("asked", { title: "Asked", time: t.asked, tone: "ok", chip: ["ok", "Passed the guard"], body: `${stop(t.words)} Asked at ${atS(t.asked, now)}.`, fact: cite(f.asked) });
      step("applied", { title: "Applied", time: t.applied, tone: "ok", chip: ["ok", "Recorded"], body: "The box made the change and wrote down what it was before and after.", fact: cite(f.applied) });
      step("watching", { title: tr ? "Watching" : "Watched", time: t.applied, tone: o.tone, chip: [o.tone, tr ? `${mmss((ms(t.decides) - ms(now)) / 1000)} left` : `${Math.round(took / 60)} minutes`], bar: [Math.min(1, Math.max(0, (ms(tr ? now : t.ended || t.decides) - ms(t.applied)) / 1000 / took)), o.tone], body: tr ? "Comparing with the same box a minute before." : stop(o.words), fact: cite(f.verdict) });
      step("decides", { title: tr ? "Decides" : "Decided", time: t.decides, tone: tr ? "idle" : o.tone, chip: tr ? ["wait", "Waiting"] : [o.tone, o.words], body: tr ? `The box decides at ${hms(t.decides)}.` : stop(o.words), fact: cite(f.verdict) });
      step("ends", { title: tr ? (t.waits_for ? `${t.waits_for} confirms` : "Kept by the timer") : o.words, time: tr ? null : t.ended, tone: o.tone, chip: tr ? ["wait", t.waits_for ? `Waits for ${t.waits_for}` : "Waiting"] : [o.tone, o.words], body: tr ? (t.waits_for ? `If it is better, ${t.waits_for} confirms it or undoes it.` : "If it is better, the timer keeps it.") : stop(o.words), fact: cite(f.verdict) });
    }

    const log = node(`${bn.id}/log`, "part", { label: "Logbook", title: "The logbook", stage: "log", rec,
      say: !rec ? `${b.unit} has sent no pages of its logbook yet. They appear here once its record reaches the controller.` : `${b.unit} signs every request, change, verdict and refusal as a page, each linked to the one before. It has ${n(rec.seq)} pages${rec.agrees === false ? " and does not agree with ours" : " and agrees with ours"}.` }, bn);
    for (const e of [...(b.logbook || [])].sort((x, y) => y.seq - x.seq)) {
      node(`${log.id}/${e.seq}`, "tile", { page: e.seq, t: e.at, line: e.words, label: `Page ${n(e.seq)}`, title: e.words, chip: [e.tone || "idle", pageWords(e)], body: `At ${atS(e.at, now)}, page ${n(e.seq)} of ${b.unit}’s logbook.`, fact: cite(e.fact) }, log);
    }

    const gname = g?.name || w?.gateway || b.gateway || "";
    const gw = node(`${bn.id}/gw`, "part", { label: "Gateway", title: gname ? `Countersigned by ${gname}` : "Its gateway", stage: "gw", w, gname,
      say: gname ? `${b.unit} tunnels through ${gname}, which sees what really crosses each line and countersigns the logbook, so the box cannot rewrite its history unseen.` : `No Weavian gateway has countersigned ${b.unit}’s logbook yet.` }, bn);
    if (w) node(`${gw.id}/witness`, "tile", { label: `Countersignature ${n(w.seq)}`, title: `Countersignature ${n(w.seq)}`, chip: w.matched === false ? ["no", "Does not match"] : w.matched ? ["ok", "Matches"] : ["ok", "Countersigned"], body: `${gname} countersigned ${b.unit}’s logbook at ${at(w.at, now)}.${w.matched === false ? " It expects a page that does not follow on from the box’s own seal." : w.matched ? " It follows on from the box’s own seal." : ""}`, fact: cite(w.fact) }, gw);
    gw.others = gateways.filter((x) => x !== g && x.witnessed?.[b.unit]);
    for (const o of gw.others) node(`${gw.id}/gateway:${o.id}`, "tile", { label: o.name, title: o.name, chip: (o.carries || []).includes(b.unit) ? ["ok", "Carries it"] : ["idle", "Countersigned before"], body: `${o.name} last countersigned ${b.unit}’s logbook at ${at(o.witnessed[b.unit].at, now)}: countersignature ${n(o.witnessed[b.unit].seq)}.`, fact: (o.facts || []).find((f) => parts(f.name)[5] === b.unit) || newest(o.facts) }, gw);
  }

  /* The gateways, each with the logbooks it countersigned. */
  for (const g of gateways) {
    const own = (g.carries || []).filter((u) => boxes.some((b) => b.unit === u)), spare = (g.spare_for || []).filter((u) => boxes.some((b) => b.unit === u));
    const gn = node(`gateway:${g.id}`, "gw", {
      gw: g, label: g.name || g.id, title: g.name || g.id, own, spare, quiet: !own.length, facts: g.facts, prov: newest(g.facts),
      say: `${g.name || g.id} is a Weavian gateway. ${own.length ? `It carries ${list(own)} and countersigns ${own.length > 1 ? "their logbooks" : "its logbook"}.` : "It carries none of your boxes now."}${spare.length ? ` It countersigned ${list(spare.map((u) => `${u}’s logbook`))} before.` : ""}`,
    }, root);
    const used = new Set();
    for (const [unit, x] of Object.entries(g.witnessed || {})) {
      const f = (g.facts || []).find((y) => parts(y.name)[5] === unit && Number(parts(y.name)[6]) === Number(x.seq));
      if (f) used.add(refKey(f));
      node(`${gn.id}/${unit}`, "tile", { label: unit, title: unit, chip: own.includes(unit) ? ["ok", "Countersigns"] : ["idle", "Countersigned before"], body: `Countersignature ${n(x.seq)}, at ${at(x.at, now)}.`, fact: f || null }, gn);
    }
    (g.facts || []).filter((f) => f?.name && !used.has(refKey(f))).forEach((f) => factTile(gn, f));
  }

  /* The lines between them, as the route sends them, each resting on its
     facts: a mandate from the key to its agent is the key's line. */
  const edges = (data.edges || []).flatMap((e) => {
    const a = NODES.get(e.from), z = NODES.get(e.to);
    if (!a || !z || a.kind === "nokey" || !a.parent || !z.parent) return [];
    const kind = e.kind === "mandate" ? (a.kind === "key" ? "issued" : "mandate") : e.kind === "spare" ? "spare" : "witness";
    return [{ id: `${e.kind}:${e.from}>${e.to}`, kind, a, z, facts: e.facts, prov: newest(e.facts) }];
  });

  /* What a person must do, soonest first. */
  const waiting = boxes.filter((b) => running(b.trial, now) && b.trial.waits_for).sort((x, y) => ms(x.trial.decides) - ms(y.trial.decides));
  const needs = waiting.length ? { unit: waiting[0].unit, t: waiting[0].trial, to: `box:${waiting[0].unit}/trial`, more: waiting.length - 1 } : null;
  const notDrawn = data.not_drawn || [];

  const who = [...new Set(boxes.flatMap((b) => holders(b.unit).map((x) => theAgent(x.a))))], may = boxes.filter((b) => holders(b.unit).length);
  const onTrial = boxes.filter((b) => running(b.trial, now)), bad = boxes.filter((b) => b.record?.agrees === false), quiet = boxes.filter((b) => b.online === false);
  const news = [onTrial.length && `a change is on trial at ${list(onTrial.map((b) => b.unit))}`, ...bad.map((b) => `${b.unit}’s logbook disagrees with ours`), ...quiet.map((b) => `${b.unit} has not reported since ${at(b.last_heard, now)}`)].filter(Boolean);
  root.say = !boxes.length ? "" : `Right now ${who.length ? `${list(who)} may change ${may.length === boxes.length ? (boxes.length === 1 ? "your box" : `all ${boxes.length} of your boxes`) : `${may.length} of your ${plural(boxes.length, "box", "boxes")}`}` : key ? "agents may only read" : "agents may only read, since no key of yours signs key cards yet"}. ${news.length ? stop(capArticle(list(news))) : boxes.length > 1 ? "Every box is calm." : "It is calm."}`;
  return { root, NODES, edges, now, needs, notDrawn, empty: !boxes.length, key, agents: root.kids.filter((x) => x.kind === "agent"), boxes: root.kids.filter((x) => x.kind === "box"), gateways: root.kids.filter((x) => x.kind === "gw") };
}
function groupBy(xs, keyOf) {
  const m = new Map();
  for (const x of xs) { const k = keyOf(x); if (!m.has(k)) m.set(k, []); m.get(k).push(x); }
  return m;
}

/* ── Text, measured once ───────────────────────────────────────────────── */
export const FONT = {
  sans: 'Inter, "SF Pro Display", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  mono: '"JetBrains Mono", "SF Mono", "Fira Code", ui-monospace, Menlo, monospace',
};
const MW = new Map();
let MC;
export const forgetMeasures = () => MW.clear();
function tw(s, px, wt = 400, mono = false) {
  const f = `${wt} ${px}px ${mono ? FONT.mono : FONT.sans}`, k = `${f}|${s}`;
  let w = MW.get(k);
  if (w === undefined) {
    if (MC === undefined) MC = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
    if (MC) { MC.font = f; w = MC.measureText(s).width; } else w = s.length * px * (mono ? 0.6 : 0.55); // no canvas: near enough to lay out
    MW.set(k, w);
  }
  return w;
}
function wrap(s, px, wt, mono, w) {
  const out = [];
  for (const para of String(s).split("\n")) {
    const sep = mono ? "" : " ", words = (mono ? para.split(/(?<=[/ ,])/) : para.split(" ")).flatMap((wd) => {
      if (tw(wd, px, wt, mono) <= w) return [wd];
      const bits = [];
      let cur = "";
      for (const ch of wd) { if (cur && tw(cur + ch, px, wt, mono) > w) { bits.push(cur); cur = ""; } cur += ch; }
      return [...bits, cur];
    });
    let cur = "";
    for (const wd of words) { const t = cur ? cur + sep + wd : wd; if (!cur || tw(t, px, wt, mono) <= w) cur = t; else { out.push(cur); cur = wd; } }
    out.push(cur);
  }
  return out;
}
export const r2 = (v) => Math.round(v * 100) / 100;
const TONE = { ok: "tok", wait: "twait", no: "tno", idle: "tidle" };

/* A pen draws SVG in a thing's own reading pixels. */
class Pen {
  constructor() { this.o = []; }
  put(type, props, kid) { this.o.push(h(type, { key: this.o.length, ...props }, kid)); }
  text(x, y, w, s, px = 13.5, wt = 400, cls = "t1", o = {}) {
    const lh = o.lh || 1.45, lines = wrap(s, px, wt, o.mono, w), className = `${cls}${o.mono ? " mono" : ""}${o.caps ? " caps" : ""}`;
    lines.forEach((ln, i) => this.put("text", { x: r2(x), y: r2(y + px * (lh / 2 + 0.33) + i * px * lh), fontSize: px, fontWeight: wt, className, textAnchor: o.end ? "end" : o.middle ? "middle" : undefined, ...o.attr }, ln));
    return lines.length * px * lh;
  }
  pillW(s, px = 12) { return tw(s, px, 600) + 18; }
  pill(x, y, s, tone, px = 12) {
    const w = this.pillW(s, px), ht = px + 9;
    this.rect(x, y, w, ht, 8, `p-${tone}`);
    this.put("text", { x: r2(x + 9), y: r2(y + ht / 2 + px * 0.36), fontSize: px, fontWeight: 600, className: TONE[tone] }, s);
    return w;
  }
  dot(x, y, tone, r = 4) { this.put("circle", { cx: r2(x), cy: r2(y), r, className: `st-${tone}` }); }
  rect(x, y, w, ht, rx, cls) { this.put("rect", { x: r2(x), y: r2(y), width: r2(Math.max(0, w)), height: r2(Math.max(0, ht)), rx, className: cls }); }
  hline(x1, x2, y) { this.put("line", { x1: r2(x1), y1: r2(y), x2: r2(x2), y2: r2(y), className: "rule" }); }
  bar(x, y, w, f, tone) { this.rect(x, y, w, 8, 4, "trk"); this.rect(x, y, Math.max(8, w * f), 8, 4, `st-${tone}`); }
  /* Chips with a dot each, in rows that wrap; long words wrap inside one. */
  chips(x, y, w, items) {
    let cx = x, cy = y;
    const ht = 26, chip = (c, cw, hh) => { this.rect(cx, cy, cw, hh, 8, `chip${c.tone === "wait" ? " c-wait" : ""}`); this.dot(cx + 12, cy + 13, c.tone); this.put("text", { x: r2(cx + 22), y: r2(cy + 17.5), fontSize: 12.5, fontWeight: 600, className: "t1" }, c.name); };
    for (const c of items) {
      const nw = tw(c.name, 12.5, 600), cw = nw + (c.words ? tw(c.words, 12.5) + 8 : 0) + 34;
      if (cx > x && cx + Math.min(cw, w) > x + w) { cx = x; cy += ht + 6; }
      if (cw > w) {
        const lines = wrap(c.words, 12.5, 400, false, w - nw - 42), hh = Math.max(ht, lines.length * 18 + 8);
        chip(c, w, hh);
        for (const [i, ln] of lines.entries()) this.put("text", { x: r2(cx + 30 + nw), y: r2(cy + 17.5 + i * 18), fontSize: 12.5, className: c.tone === "wait" ? "twait" : "t2" }, ln);
        cx = x; cy += hh + 6;
        this.end = x + w;
        continue;
      }
      chip(c, cw, ht);
      if (c.words) this.put("text", { x: r2(cx + 30 + nw), y: r2(cy + 17.5), fontSize: 12.5, className: c.tone === "wait" ? "twait" : "t2" }, c.words);
      cx += cw + 6;
    }
    this.end = cx > x ? cx - 6 : x + w;
    return items.length ? cy + ht - y : 0;
  }
  out() { return this.o; }
}
const GLYPH = { ai: ["M0-6 1.7-1.7 6 0 1.7 1.7 0 6-1.7 1.7-6 0-1.7-1.7Z", "glf"], key: ["M-2.5-3a3 3 0 1 0 0 6 3 3 0 1 0 0-6ZM0.5 0H6.5M4 0V2.6M6.5 0V2.2", "gl"], gw: ["M-5.5-6h11v3.4h-11ZM-5.5-1.7h11v3.4h-11ZM-5.5 2.6h11v3.4h-11Z", "gl"] };
const glyph = (pen, k, cx, cy) => { pen.put("circle", { cx, cy, r: 13, className: "chip" }); pen.put("path", { d: GLYPH[k][0], className: GLYPH[k][1], transform: `translate(${cx} ${cy}) scale(1.35)` }); };

/* ── Level one: the customer ───────────────────────────────────────────── */
function boxCard(nd, w) {
  const b = nd.b, pen = new Pen(), x = 20, iw = w - 36, [tone, word] = nd.pill, rec = b.record;
  let y = 16;
  const pw = pen.pillW(word), one = tw(b.unit, 17, 600) + 12 + pw <= iw;
  pen.text(x, y, iw - (one ? pw + 10 : 0), b.unit, 17, 600, "t1");
  if (one) pen.pill(x + iw - pw, y + 1, word, tone); else { y += 28; pen.pill(x, y, word, tone); }
  y += 27;
  if (nd.role) { pen.text(x + iw, y, 120, "Look inside ›", 12.5, 600, "look", { end: 1 }); y += pen.text(x, y, iw - 110, nd.role, 13.5, 400, "t2") + 10; }
  const rows = [["Who", nd.who.length ? nd.who.join(". ") : "No agent may change it now"],
    ["Logbook", !rec ? "No pages copied yet" : rec.agrees === false ? "Disagrees with ours" : b.online === false ? `Agreed with ours at page ${n(rec.seq)} when last heard` : `Agrees with ours at page ${n(rec.seq)}`]];
  for (const [lab, val] of rows) { pen.text(x, y + 1.5, 70, lab.toUpperCase(), 10.5, 600, "t3", { caps: 1 }); y += pen.text(x + 72, y, iw - 72, val, 13.5, 400, "t1") + 6; }
  let room = false;
  if (nd.lines.length) {
    pen.text(x, y + 6, 70, "LINES", 10.5, 600, "t3", { caps: 1 });
    const ht = pen.chips(x + 72, y, iw - 72, nd.lines.map((l) => ({ ...l, words: l.brief })));
    room = pen.end + 12 + tw("Look inside ›", 12.5, 600) <= x + iw;
    if (!nd.role && room) pen.text(x + iw, y + 4, 120, "Look inside ›", 12.5, 600, "look", { end: 1 });
    y += ht;
  }
  if (!nd.role && !room) { y += 8; pen.text(x + iw, y, 120, "Look inside ›", 12.5, 600, "look", { end: 1 }); y += 12; }
  return { h: y + 16, m: pen.out(), stripe: tone };
}
function smallCard(w, kind, title, lines, { quiet, opens = true } = {}) {
  const pen = new Pen(), wide = w >= 200, g = kind && wide, x = g ? 54 : 14, iw = w - x - (wide ? 16 : 12);
  if (g) glyph(pen, kind, 30, 30);
  let y = 14;
  y += pen.text(x, y, iw - (wide && opens ? 86 : 0), title, quiet ? 14.5 : 15, 600, quiet ? "t2" : "t1");
  if (wide && opens) pen.text(x + iw, 15, 86, "Look inside ›", 12, 600, "look", { end: 1 });
  lines.forEach(([s, cls]) => { y += 3 + pen.text(x, y + 3, iw, s, 13, 400, cls); });
  return { h: Math.max(y + 14, g ? 60 : 0), m: pen.out() };
}
const gwLines = (nd) => [[`Countersigns the logbook${nd.own.length > 1 ? "s" : ""} of ${list(nd.own)}`, "t1"]];
const spareLine = (nd) => `Countersigned ${list(nd.spare.length ? nd.spare : Object.keys(nd.gw.witnessed || {}))} before`;
/* The boxes nothing signed backs yet, named in one line under the others. */
export const notDrawnLine = (m) => `Not drawn yet: ${list(m.notDrawn.map((x) => `${x.unit} (${lowerFirst(String(x.why || "nothing it signed has reached us").replace(/\.$/, ""))})`))}.`;

function labLayout(m, cx) {
  const P = cx.P, slots = [], decor = new Pen(), keyNd = m.root.kids.find((x) => x.id === "key");
  const put = (nd, x, y, w, sum, frame, stripe) => { slots.push({ nd, x, y, w, h: sum.h, sum: sum.m, frame, stripe }); return sum.h; };
  const mayAg = m.agents.filter((a) => !a.quiet), quietAg = m.agents.filter((a) => a.quiet);
  const main = m.gateways.filter((g) => g.own.length), spares = m.gateways.filter((g) => !g.own.length);
  const ksum = smallCard(P ? 368 : 272, "key", keyNd.title, [[keyNd.sub, "t2"]], { quiet: keyNd.kind === "nokey", opens: keyNd.kind !== "nokey" });
  const agentSum = (a, w) => smallCard(w, "ai", a.title, [[a.sub, "t2"]], { quiet: a.quiet, opens: a.kids.length > 0 });
  let y;
  if (P) {
    put(keyNd, 0, 0, 368, ksum, keyNd.kind === "nokey" ? "quiet" : "card");
    y = ksum.h + 40;
    for (let i = 0; i < quietAg.length; i += 2) {
      const row = quietAg.slice(i, i + 2), sums = row.map((a) => agentSum(a, 178)), hh = Math.max(...sums.map((s) => s.h));
      for (const [j, a] of row.entries()) { sums[j].h = hh; put(a, j * 190, y, 178, sums[j], "quiet"); }
      y += hh + 24;
    }
    for (const a of mayAg) { y += put(a, 22, y, 346, agentSum(a, 346), "card") + 14; }
    y += mayAg.length ? 22 : 0;
    for (const b of m.boxes) { const s = boxCard(b, 346); y += put(b, 22, y, 346, s, "card", s.stripe) + 14; }
    y += 12;
    for (const g of main) { y += put(g, 22, y, 346, smallCard(346, "gw", g.title, gwLines(g)), "card") + 12; }
    if (spares.length) {
      decor.text(22, y + 2, 346, "OTHER GATEWAYS", 11, 600, "t3", { caps: 1 });
      y += 26;
      for (let i = 0; i < spares.length; i += 2) {
        const row = spares.slice(i, i + 2), sums = row.map((g) => smallCard(170, null, g.title, [[spareLine(g), "t2"]], { quiet: 1 })), hh = Math.max(...sums.map((s) => s.h));
        for (const [j, g] of row.entries()) { sums[j].h = hh; put(g, 22 + j * 176, y, 170, sums[j], "quiet"); }
        y += hh + 6;
      }
    }
    let hh = Math.max(...slots.map((s) => s.y + s.h));
    if (m.notDrawn.length) hh += 18 + decor.text(22, hh + 18, 346, notDrawnLine(m), 13, 400, "t3");
    return { x0: 0, w: 368, h: hh, decor: decor.out(), slots, bands: 1 };
  }
  /* Desktop: boxes in bands of three, each band's gateways under the boxes
     they carry, so the tunnels go straight down; the agents above them. */
  const bw = 320, step = 360, C = 3, bands = [];
  for (let i = 0; i < m.boxes.length; i += C) bands.push(m.boxes.slice(i, i + C));
  const W0 = Math.max(Math.min(m.boxes.length, C) * step - 40, 272);
  const aw = Math.max(272, ...m.agents.map((a) => 54 + tw(a.title, 15, 600) + 112)), half = Math.ceil(quietAg.length / 2);
  const row = [...quietAg.slice(0, half), ...mayAg, ...quietAg.slice(half)], rowW = row.length * aw + Math.max(0, row.length - 1) * 64;
  const W = Math.max(W0, rowW), mid = W / 2, x0 = (n2) => mid - (n2 * step - 40) / 2;
  const asums = row.map((a) => agentSum(a, aw)), ah = Math.max(0, ...asums.map((s) => s.h));
  const rowX = mid - rowW / 2, mayX = mayAg.length ? mayAg.map((a) => rowX + row.indexOf(a) * (aw + 64) + aw / 2) : [mid];
  const kx = mayX.reduce((s, v) => s + v, 0) / mayX.length;
  put(keyNd, kx - 136, 0, 272, ksum, keyNd.kind === "nokey" ? "quiet" : "card");
  const yA = ksum.h + 52;
  row.forEach((a, i) => { asums[i].h = ah; put(a, rowX + i * (aw + 64), yA, aw, asums[i], a.quiet ? "quiet" : "card"); });
  y = yA + (row.length ? ah + 62 : 0);
  const placedGw = new Set();
  // Where the last band's gateways end, and the row they sit in.
  const lastRow = [];
  let rowY = y;
  for (const band of bands) {
    const sums = band.map((b) => boxCard(b, bw)), bh = Math.max(...sums.map((s) => s.h)), bx = x0(band.length);
    for (const [i, b] of band.entries()) put(b, bx + i * step, y, bw, sums[i], "card", sums[i].stripe);
    const gs = main.filter((g) => !placedGw.has(g) && band.some((b) => g.own.includes(b.b.unit)));
    const gy = y + bh + 56, gw = 272;
    let gh = 0, last = -Infinity;
    lastRow.length = 0;
    gs.map((g) => { const xs = band.map((b, i) => (g.own.includes(b.b.unit) ? bx + i * step + bw / 2 : null)).filter((v) => v !== null); return [g, (Math.min(...xs) + Math.max(...xs)) / 2 - gw / 2]; })
      .sort((p, q) => p[1] - q[1]).forEach(([g, gx]) => {
        const xg = Math.max(gx, last + gw + 16), s = smallCard(gw, "gw", g.title, gwLines(g));
        last = xg; placedGw.add(g); lastRow.push(xg + gw); gh = Math.max(gh, put(g, xg, gy, gw, s, "card"));
      });
    rowY = gy;
    y = gs.length ? gy + gh + 62 : y + bh + 62;
  }
  for (const g of main.filter((x) => !placedGw.has(x))) { y += put(g, mid - 136, y, 272, smallCard(272, "gw", g.title, gwLines(g)), "card") + 16; }
  if (spares.length) {
    // In the free end of the last gateway row when it has room, so the picture
    // keeps its width; else beside it.
    const free = W - Math.max(0, ...lastRow.map((x) => x + 24)), fits = free >= 180;
    const sw = fits ? Math.min(272, free) : 272, sx = fits ? W - sw : Math.max(W, ...slots.map((sl) => sl.x + sl.w)) + 40;
    let sy = rowY;
    decor.text(sx, rowY - 20, sw, "OTHER GATEWAYS", 11, 600, "t3", { caps: 1 });
    for (const g of spares) sy += put(g, sx, sy, sw, smallCard(sw, null, g.title, [[spareLine(g), "t2"]], { quiet: 1 }), "quiet") + 12;
  }
  const xs = slots.map((s) => s.x), x1 = Math.min(0, ...xs), x2 = Math.max(W, ...slots.map((s) => s.x + s.w));
  let hh = Math.max(...slots.map((s) => s.y + s.h));
  if (m.notDrawn.length) { const bx = mid - (Math.min(m.boxes.length, C) * step - 40) / 2; hh += 22 + decor.text(bx, hh + 22, Math.max(W0, 600), notDrawnLine(m), 13, 400, "t3"); }
  return { x0: x1, w: x2 - x1, h: hh, decor: decor.out(), slots, bands: bands.length };
}

/* The lines between level-one things, from the edges the route sends. */
function labEdges(m, cx, L) {
  const E = [], P = cx.P, R = (nd) => nd.r, mayCount = (agent) => agent.kids.filter((k) => k.id.startsWith(`${agent.id}/card:`)).length;
  const byKind = (k) => m.edges.filter((e) => e.kind === k);
  for (const e of byKind("issued")) {
    const k = R(e.a), a = R(e.z), sx = k.x + k.w / 2, sy = k.y + k.h, ex = a.x + 20 < sx && sx < a.x + a.w - 20 ? sx : a.x + a.w / 2, ey = a.y, my = (sy + ey) / 2, c = mayCount(e.z);
    E.push({ e, cls: "e-card", d: Math.abs(sx - ex) < 1 ? `M${r2(sx)} ${r2(sy)}V${r2(ey)}` : `M${r2(sx)} ${r2(sy)}C${r2(sx)} ${r2(my)} ${r2(ex)} ${r2(my)} ${r2(ex)} ${r2(ey)}`, arrow: [ex, ey],
      label: c ? `${plural(c, "key card", "key cards")} in force` : "No key card in force", lx: (sx + ex) / 2 + 10, ly: P ? sy + 22 : my + 4 });
  }
  const mand = byKind("mandate"), wit = byKind("witness"), spare = byKind("spare");
  if (P || L.bands > 1) {
    /* A trunk down the left, a branch into each box it reaches. */
    const x0 = P ? 9 : L.x0 - 18, agents = [...new Set(mand.map((e) => e.a))];
    agents.forEach((ag, i) => {
      const a = R(ag), tx = x0 - 8 * i, mine = mand.filter((e) => e.a === ag), top = a.y + a.h;
      const lastY = Math.max(...mine.map((e) => (P ? R(e.z).y + 30 : R(e.z).y - 14)));
      E.push({ e: mine[0], cls: "e-card", d: `M${r2(a.x + 14)} ${r2(top)}V${r2(top + 16)}H${r2(tx)}V${r2(lastY)}` });
      for (const e of mine) {
        const b = R(e.z);
        E.push(P ? { e, cls: "e-card", d: `M${r2(tx)} ${r2(b.y + 30)}H${r2(b.x - 2)}`, arrow: [b.x, b.y + 30, "r"] }
          : { e, cls: "e-card", d: `M${r2(tx)} ${r2(b.y - 14)}H${r2(b.x + b.w / 2)}V${r2(b.y)}`, arrow: [b.x + b.w / 2, b.y] });
      }
    });
  } else {
    const per = new Map();
    for (const e of mand) { if (!per.has(e.a)) per.set(e.a, []); per.get(e.a).push(e); }
    for (const [ag, mine] of per) {
      const a = R(ag);
      mine.sort((p, q) => R(p.z).x - R(q.z).x).forEach((e, i) => {
        const b = R(e.z), sx = a.x + a.w * (0.25 + 0.5 * (mine.length > 1 ? i / (mine.length - 1) : 0.5)), sy = a.y + a.h, ex = b.x + b.w / 2, ey = b.y, my = (sy + ey) / 2;
        E.push({ e, cls: "e-card", d: `M${r2(sx)} ${r2(sy)}C${r2(sx)} ${r2(my)} ${r2(ex)} ${r2(my)} ${r2(ex)} ${r2(ey)}`, arrow: [ex, ey] });
      });
    }
  }
  /* A tunnel straight down from each box to the gateway carrying it; the
     gateways it could also use only while it or they are pointed at. */
  const per = new Map();
  for (const e of wit) { if (!per.has(e.z)) per.set(e.z, []); per.get(e.z).push(e); }
  if (P) {
    [...per.keys()].forEach((g, gi) => {
      const gr = R(g), tx = 368 + 9 + 6 * gi;
      for (const e of per.get(g)) { const b = R(e.a); E.push({ e, cls: "e-tun", d: `M${r2(b.x + b.w)} ${r2(b.y + 30)}H${r2(tx)}V${r2(gr.y + 30)}H${r2(gr.x + gr.w)}` }); }
    });
  } else {
    for (const [g, mine] of per) {
      const gr = R(g);
      mine.sort((p, q) => R(p.a).x - R(q.a).x).forEach((e, i) => {
        const b = R(e.a), sx = b.x + b.w / 2, sy = b.y + b.h, ex = gr.x + gr.w * (mine.length > 1 ? 0.3 + (0.4 * i) / (mine.length - 1) : 0.5), ey = gr.y, my = (sy + ey) / 2;
        E.push({ e, cls: "e-tun", d: `M${r2(sx)} ${r2(sy)}C${r2(sx)} ${r2(my)} ${r2(ex)} ${r2(my)} ${r2(ex)} ${r2(ey)}` });
      });
    }
  }
  for (const e of spare) {
    const b = R(e.a), gr = R(e.z), sx = b.x + b.w, sy = b.y + b.h * 0.75, ex = gr.x, ey = gr.y + gr.h / 2;
    E.push({ e, cls: "e-spare", hot: [e.a.id, e.z.id], d: `M${r2(sx)} ${r2(sy)}C${r2((sx + ex) / 2)} ${r2(sy)} ${r2((sx + ex) / 2)} ${r2(ey)} ${r2(ex)} ${r2(ey)}` });
  }
  return E;
}

/* ── Level two: a box, as the path a change takes ──────────────────────── */
function stageCard(p, w, cx) {
  const pen = new Pen(), x = 18, iw = w - 32, now = cx.now;
  let y = 14, stripe = "ok";
  const title = (s, pill) => { const pw = pill ? pen.pillW(pill[1]) : 0, ht = pen.text(x, y, iw - (pw ? pw + 8 : 0), s, 15, 600, "t1"); if (pill) pen.pill(x + iw - pw, y - 1, pill[1], pill[0]); y += Math.max(ht, 22) + 6; };
  if (p.stage === "menu") {
    title("Who may ask");
    p.rows.forEach((r) => { pen.dot(x + 4, y + 10, r.tone); y += pen.text(x + 16, y, iw - 16, r.line, 13.5) + 6; });
    if (!p.rows.length) y += pen.text(x, y, iw, "No agent has asked anything here.", 13.5, 400, "t2") + 6;
    stripe = p.rows.some((r) => r.tone === "ok") ? "ok" : "idle";
  } else if (p.stage === "guard") {
    title("The guard");
    y += pen.text(x, y, iw, `Today ${p.through} through, ${p.stopped} stopped`, 13.5, 400, "t2") + 4;
    let q = "";
    p.kids.forEach((c) => {
      if (c.q !== q) { q = c.q; y += 6; y += pen.text(x, y, iw, q.toUpperCase(), 10.5, 600, "t3", { caps: 1 }) + 2; }
      const [tone, mark] = c.mark, mw = tw(mark, 12.5, 600) + 2;
      pen.text(x, y, 18, String(c.num), 12.5, 500, "t3", { mono: 1 });
      const ht = pen.text(x + 18, y, iw - 18 - mw - 8, c.name, 13.5, 400, "t1");
      pen.text(x + iw, y + 0.5, mw, mark, 12.5, 600, tone === "idle" ? "t3" : TONE[tone], { end: 1 });
      y += ht + 3;
    });
    stripe = p.stopped ? "no" : p.through ? "ok" : "idle";
  } else if (p.stage === "trial") {
    const t = p.t;
    if (!t) { title("The trial"); y += pen.text(x, y, iw, "Nothing is on trial here.", 13.5, 400, "t2") + 6; stripe = "idle"; }
    else {
      const tr = running(t, now), o = t.outcome || { tone: tr ? "wait" : "idle", words: tr ? "On trial" : "Ended" }, took = (ms(t.decides) - ms(t.applied)) / 1000;
      title("The trial", [o.tone, tr ? "On trial" : o.words]);
      y += pen.text(x, y, iw, `${stop(t.words)}${tr ? "" : ` At ${at(t.asked, now)}.`}`, 13.5) + 8;
      pen.bar(x, y, iw, Math.min(1, Math.max(0, (ms(tr ? now : t.ended || t.decides) - ms(t.applied)) / 1000 / took)), o.tone); y += 14;
      y += pen.text(x, y, iw, tr ? `${mmss((ms(t.decides) - ms(now)) / 1000)} left. Decides at ${hms(t.decides)}.` : `Decided at ${hms(t.decides)}.`, 13, 500, tr ? "twait" : "t2") + 6;
      if (t.rule) y += pen.text(x, y, iw, t.rule, 13, 400, "t2");
      stripe = o.tone;
    }
  } else if (p.stage === "log") {
    const rec = p.rec, b = p.parent.b;
    title("The logbook", !rec ? ["idle", "Nothing yet"] : rec.agrees === false ? ["no", "Disagrees"] : b.online === false ? ["idle", "Not heard"] : ["ok", "Agrees"]);
    y += pen.text(x, y, iw, !rec ? "No pages copied yet." : b.online === false ? `${n(rec.seq)} pages when last heard.` : `${n(rec.seq)} pages. ${rec.agrees === false ? "Does not agree with ours." : "Agrees with ours."}`, 13.5, 400, "t2") + 8;
    p.kids.slice(0, 3).forEach((c) => {
      pen.hline(x, x + iw, y); y += 7;
      pen.text(x, y, 60, hm(c.t), 12.5, 500, "t3", { mono: 1 });
      const pw = pen.pillW(c.chip[1], 11.5);
      pen.pill(x + iw - pw, y - 2, c.chip[1], c.chip[0], 11.5); y += 20;
      y += pen.text(x, y, iw, c.line, 13.5) + 6;
    });
    stripe = !rec ? "idle" : rec.agrees === false ? "no" : b.online === false ? "idle" : "ok";
  } else {
    const w2 = p.w;
    title(p.title);
    if (w2) {
      y += pen.text(x, y, iw, `Countersignature ${n(w2.seq)}, at ${at(w2.at, now)}`, 13.5) + 4;
      if (w2.matched !== undefined) y += pen.text(x, y, iw, w2.matched ? "It follows on from the box’s own seal." : "It expects a page that does not follow on from the box’s own seal.", 13.5, 400, w2.matched ? "t2" : "tno") + 4;
    } else y += pen.text(x, y, iw, "No countersignature yet.", 13.5, 400, "t2") + 4;
    if (p.others.length) y += pen.text(x, y, iw, `Also countersigned by ${list(p.others.map((g) => g.name || g.id))}`, 13.5, 400, "t2") + 4;
    stripe = !w2 ? "idle" : w2.matched === false ? "no" : "ok";
  }
  if (p.kids.length) pen.text(x, y + 6, iw, "Look inside ›", 12, 600, "look");
  return { h: y + 30, m: pen.out(), stripe };
}
function flowLayout(nd, cx) {
  const fit = Math.floor(((cx.width - 32) / 0.92 - 32 - 4 * 34) / 5), P = cx.P || fit < 190;
  const sw = P ? 340 : Math.min(250, fit), gap = P ? 30 : 34, W = P ? 340 : 5 * sw + 4 * gap, deco = new Pen(), slots = [];
  let y = deco.text(0, 0, W, `How a change reaches ${nd.b.unit}`, 18, 600, "t1") + 14;
  const sums = nd.kids.map((p) => stageCard(p, sw, cx)), mh = Math.max(...sums.map((s) => s.h));
  nd.kids.forEach((p, i) => {
    const sx = P ? 0 : i * (sw + gap);
    slots.push({ nd: p, x: sx, y, w: sw, h: sums[i].h, sum: sums[i].m, frame: "card" });
    if (i < nd.kids.length - 1) {
      if (P) { const ay = y + sums[i].h + 6; deco.put("path", { className: "arw", d: `M${sw / 2} ${ay}V${ay + gap - 12}M${sw / 2 - 5} ${ay + gap - 17}L${sw / 2} ${ay + gap - 12}L${sw / 2 + 5} ${ay + gap - 17}` }); }
      else { const ax = sx + sw + 6, ay = y + 24; deco.put("path", { className: "arw", d: `M${ax} ${ay}H${ax + gap - 12}M${ax + gap - 17} ${ay - 5}L${ax + gap - 12} ${ay}L${ax + gap - 17} ${ay + 5}` }); }
    }
    if (P) y += sums[i].h + gap;
  });
  y += P ? 2 - gap + 22 : mh + 22;
  if (nd.lines.length) { deco.text(0, y + 6, 60, "LINES", 10.5, 600, "t3", { caps: 1 }); y += deco.chips(56, y, W - 56, nd.lines); }
  return { w: W, h: y, decor: deco.out(), slots };
}

/* ── Level three and four: inside a stage, a section, a fact ──────────── */
const W3 = (cx) => (cx.P ? 340 : 640);
function intro(pen, W, head, sub) { let y = pen.text(0, 0, W, head, 18, 600, "t1") + 6; if (sub) y += pen.text(0, y, W, sub, 13.5, 400, "t2") + 4; return y + 10; }
/* A row: title and pill, then its words. */
function rowCard(c, w) {
  const pen = new Pen(), x = 14, iw = w - 28, pw = c.chip ? pen.pillW(c.chip[1]) : 0, one = !pw || pw + 120 <= iw;
  let y = 12;
  const th = pen.text(x, y, iw - (one && pw ? pw + 10 : 0) - (c.fact ? 14 : 0), c.title, 14.5, 600, "t1");
  if (pw) { if (one) pen.pill(x + iw - pw - (c.fact ? 14 : 0), y - 1, c.chip[1], c.chip[0]); else { y += th + 4; pen.pill(x, y, c.chip[1], c.chip[0]); } }
  y += one ? Math.max(th, 20) + 4 : 26;
  if (c.bar) { pen.bar(x, y + 2, iw, c.bar[0], c.bar[1]); y += 16; }
  if (c.body) y += pen.text(x, y, iw - (c.fact ? 14 : 0), c.body, 13.5, 400, "t2") + 2;
  if (c.fact) pen.text(x + iw + 2, 12, 12, "›", 16, 600, "t3", { end: 1 });
  return { h: y + 12, m: pen.out() };
}
function rowsLayout(nd, cx, head, sub) {
  const W = W3(cx), deco = new Pen(), slots = [];
  let y = intro(deco, W, head || nd.title, sub);
  nd.kids.forEach((c) => { const r = rowCard(c, W); slots.push({ nd: c, x: 0, y, w: W, h: r.h, sum: r.m, frame: "row" }); y += r.h + 10; });
  return { w: W, h: y - 10, decor: deco.out(), slots };
}
/* Who may ask: each key card drawn as a card. */
function keyCardObj(c, w) {
  const pen = new Pen(), x = 20, iw = w - 36;
  let y = 14;
  pen.text(x, y, iw - 140, c.label, 12.5, 500, "t3", { mono: 1 });
  const pw = pen.pillW(c.chip[1]);
  pen.pill(x + iw - pw, y - 2, c.chip[1], c.chip[0]);
  y += 24;
  y += pen.text(x, y, iw, c.title, 15, 600, "t1") + 4;
  c.lines.forEach((s) => { y += pen.text(x, y, iw, s, 13.5, 400, "t2"); });
  return { h: y + 16, m: pen.out(), stripe: c.chip[0] };
}
function whoLayout(nd, cx) {
  const b = nd.parent.b, W = W3(cx), deco = new Pen(), slots = [];
  let y = intro(deco, W, `Who may ask ${b.unit}`, "Each key card comes from your key, through Weavian’s console. A narrower card never reaches further than the one it came from.");
  nd.kids.forEach((c) => { const r = keyCardObj(c, W); slots.push({ nd: c, x: 0, y, w: W, h: r.h, sum: r.m, frame: "card", stripe: r.stripe }); y += r.h + 12; });
  const rest = nd.rows.filter((r) => r.said).map((r) => r.said);
  if (rest.length) y += deco.text(0, y + 2, W, rest.join(" "), 13.5, 400, "t2") + 2;
  return { w: W, h: y, decor: deco.out(), slots };
}
/* The guard: eight rows under the three questions. */
function guardLayout(nd, cx) {
  const b = nd.parent.b, W = W3(cx), deco = new Pen(), slots = [];
  let y = intro(deco, W, `The guard at ${b.unit}`, `Every request meets eight checks, and the box stops at the first no. ${nd.through + nd.stopped ? `Today ${nd.through} through, ${nd.stopped} stopped.` : "Nothing asked to change it today."}`), q = "";
  nd.kids.forEach((c) => {
    if (c.q !== q) { q = c.q; y += deco.text(0, y + 4, W, q.toUpperCase(), 11, 600, "t3", { caps: 1 }) + 10; }
    const pen = new Pen(), pw = pen.pillW(c.chip[1]), x = 12, iw = W - 24;
    pen.text(x, 10, 22, String(c.num), 13, 500, "t3", { mono: 1 });
    const th = pen.text(x + 26, 10, iw - 26 - pw - 10, c.name, 14.5, 600, "t1");
    pen.pill(x + iw - pw, 9, c.chip[1], c.chip[0]);
    const ht = 10 + Math.max(th, 22) + 4 + pen.text(x + 26, 10 + Math.max(th, 22) + 4, iw - 26, c.last, 13.5, 400, "t2") + 10;
    slots.push({ nd: c, x: 0, y, w: W, h: ht, sum: pen.out(), frame: "row" });
    y += ht + 8;
  });
  return { w: W, h: y - 8, decor: deco.out(), slots };
}
/* The trial as a stepper, in time order. */
function stepperLayout(nd, cx) {
  const b = nd.parent.b, t = nd.t, tr = running(t, cx.now), P = cx.P, W = W3(cx), deco = new Pen(), slots = [];
  let y = intro(deco, W, `The trial at ${b.unit}`, stop(t.words));
  const widths = P ? null : [104, 104, 196, 112, 124];
  if (!P) deco.put("line", { x1: 10, y1: y + 9, x2: W - 10, y2: y + 9, className: "rule" });
  let sx = 0;
  nd.kids.forEach((c, i) => {
    const pen = new Pen(), w = P ? W : widths[i], x = P ? 34 : 4, tone = c.tone;
    if (P) pen.dot(12, 13, tone, 7); else pen.dot(10, 9, tone, 7);
    let yy = P ? 4 : 26;
    yy += pen.text(x, yy, w - x - 6, c.title.toUpperCase(), 11, 600, tone === "idle" ? "t3" : TONE[tone], { caps: 1 }) + 2;
    if (c.time) yy += pen.text(x, yy, w - x - 6, hms(c.time), 13, 500, "t1", { mono: 1 }) + 2;
    if (c.bar) {
      pen.bar(x, yy + 3, w - x - 12, c.bar[0], c.bar[1]);
      if (tr) pen.put("line", { x1: r2(x + (w - x - 12) * c.bar[0]), y1: r2(yy - 1), x2: r2(x + (w - x - 12) * c.bar[0]), y2: r2(yy + 15), strokeWidth: 2, className: "now" });
      yy += 16;
    }
    yy += pen.text(x, yy + 2, w - x - 6, c.chip[1], 12.5, 500, "t2") + 4;
    if (P) { yy = Math.max(yy, 28); if (i < nd.kids.length - 1) deco.put("line", { x1: 12, y1: r2(y + 22), x2: 12, y2: r2(y + yy + 12), className: "rule" }); }
    slots.push({ nd: c, x: P ? 0 : sx, y, w, h: yy + 8, sum: pen.out(), frame: "step" });
    if (P) y += yy + 14; else sx += w;
  });
  if (!P) y += Math.max(...slots.map((s) => s.h)) + 14;
  if (t.rule) y += deco.text(0, y, W, t.rule, 13.5, 400, "t1") + 14;
  if (tr && t.waits_for) y += deco.text(0, y, W, `${t.waits_for} confirms it or undoes it before ${hm(t.decides)}. Until then it is on trial, and the box puts it back by itself if it is worse.`, 13.5, 400, "twait");
  return { w: W, h: y, decor: deco.out(), slots };
}
/* The logbook as a ledger: page, time, what it records, newest first. */
function ledgerLayout(nd, cx) {
  const b = nd.parent.b, P = cx.P, W = W3(cx), deco = new Pen(), slots = [], flags = nd.rec?.flags || [];
  let y = intro(deco, W, `${b.unit}’s logbook`, "Newest first. The box signs each page and links it to the one before.");
  flags.forEach((f) => { y += deco.text(0, y, W, `${stop(cap(f.why))}${f.at ? ` Found ${at(f.at, cx.now)}.` : ""}`, 13.5, 500, "tno") + 6; });
  if (!P) { [["PAGE", 58, 1], ["TIME", 74], ["WHAT IT RECORDS", 134]].forEach(([s, x, end]) => deco.text(x, y, 200, s, 10.5, 600, "t3", { caps: 1, end })); y += 22; }
  deco.hline(0, W, y);
  nd.kids.forEach((c) => {
    const pen = new Pen(), pw = pen.pillW(c.chip[1]);
    let ht;
    if (P) {
      pen.text(10, 10, 160, `${n(c.page)}  ${hm(c.t)}`, 12.5, 500, "t3", { mono: 1 });
      pen.pill(W - 10 - pw, 8, c.chip[1], c.chip[0]);
      ht = 34 + pen.text(10, 34, W - 20, c.line, 13.5, 500, "t1") + 10;
    } else {
      pen.text(58, 11, 58, n(c.page), 12.5, 500, "t3", { mono: 1, end: 1 });
      pen.text(74, 11, 60, hm(c.t), 12.5, 500, "t2", { mono: 1 });
      const th = pen.text(134, 10, W - 134 - pw - 18, c.line, 13.5, 500, "t1");
      pen.pill(W - 8 - pw, 8, c.chip[1], c.chip[0]);
      ht = Math.max(th, 22) + 20;
    }
    pen.hline(0, W, ht);
    slots.push({ nd: c, x: 0, y, w: W, h: ht, sum: pen.out(), frame: "line" });
    y += ht;
  });
  return { w: W, h: y, decor: deco.out(), slots };
}
/* A signed fact: what it says, who signed it, its name and fingerprint, its
   links, as GET /api/v1/facts answered it, or why it is not shown. */
function factLayout(c, cx) {
  const got = cx.facts.get(refKey(c.fact)), W = cx.P ? 340 : 620, pen = new Pen();
  let y = 0;
  if (c.chip) { pen.pill(0, 0, c.chip[1], c.chip[0]); y = 30; }
  y += pen.text(0, y, W, c.title, 18, 600, "t1") + 4;
  if (c.body) y += pen.text(0, y, W, c.body, 13.5, 400, "t2") + 6;
  pen.hline(0, W, y + 6); y += 18;
  const rows = isHeld(c.fact.name) ? heldRows(c.fact, cx.now) : got?.fact ? factRows(got, cx.now)
    : [{ label: "Its name", value: c.fact.name, mono: true }, { label: got?.error ? "Not shown" : "Reading", value: got?.error || "Reading the signed fact from the controller…" }];
  rows.forEach((r) => {
    y += pen.text(0, y, W, r.label.toUpperCase(), 10.5, 600, "t3", { caps: 1 }) + 3;
    if (r.links) r.links.forEach((l) => { y += pen.text(0, y, W, `→ ${l.text}`, 13.5, 500, "lnk", { attr: { "data-ref": l.ref, tabIndex: 0, role: "link" } }) + 2; });
    else y += pen.text(0, y, W, r.value, r.mono ? 13 : 13.5, 400, "t1", { mono: r.mono }) + 2;
    y += 12;
  });
  return { w: W, h: y - 12, decor: pen.out(), slots: [] };
}
/* What a thing shows when you zoom into it. */
function interiorOf(nd, cx) {
  if (nd.kind === "box") return flowLayout(nd, cx);
  if (nd.kind === "key" || nd.kind === "agent" || nd.kind === "gw") return nd.kids.length ? rowsLayout(nd, cx, nd.title, nd.say) : null;
  if (nd.kind === "part") {
    if (!nd.kids.length) return null;
    return { menu: whoLayout, guard: guardLayout, trial: stepperLayout, log: ledgerLayout }[nd.stage]?.(nd, cx) || rowsLayout(nd, cx, nd.title, nd.say);
  }
  return nd.fact ? factLayout(nd, cx) : null;
}
/* Places a thing's inside within its card, scaled so it reads at its own
   size when the card is zoomed to it, and so on down. */
function place(nd, cx) {
  const L = interiorOf(nd, cx);
  nd.L = L;
  if (!L) return;
  const pd = 14 * nd.S, ax = nd.r.x + pd, ay = nd.r.y + pd, aw = nd.r.w - 2 * pd, ah = nd.r.h - 2 * pd, IS = Math.min(aw / L.w, ah / L.h, nd.S * 0.5);
  nd.IS = IS;
  nd.ir = { x: ax + (aw - L.w * IS) / 2, y: ay + (ah - L.h * IS) / 2, w: L.w * IS, h: L.h * IS };
  const q = 16 * IS;
  nd.irPad = { x: nd.ir.x - q, y: nd.ir.y - q, w: nd.ir.w + 2 * q, h: nd.ir.h + 2 * q };
  for (const sl of L.slots) {
    Object.assign(sl.nd, { S: IS, r: { x: nd.ir.x + sl.x * IS, y: nd.ir.y + sl.y * IS, w: sl.w * IS, h: sl.h * IS }, sum: sl.sum, frame: sl.frame, stripe: sl.stripe });
    place(sl.nd, cx);
  }
}

/* Lays the whole fabric out for a screen width wide: portrait stacks every
   level. facts holds the signed facts read so far, by name#digest. */
export function layoutFabric(m, { portrait, width = 1440 }, facts) {
  const cx = { P: portrait, width, now: m.now, facts };
  const L = labLayout(m, cx);
  Object.assign(m.root, { S: 1, IS: 1, L, ir: { x: L.x0, y: 0, w: L.w, h: L.h } });
  for (const sl of L.slots) {
    Object.assign(sl.nd, { S: 1, r: { x: sl.x, y: sl.y, w: sl.w, h: sl.h }, sum: sl.sum, frame: sl.frame, stripe: sl.stripe });
    place(sl.nd, cx);
  }
  // A small margin round the picture; room for the key cards' trunk when the
  // boxes take more than one band, and for the phone's tunnels at its right.
  const mx = portrait ? 12 : 8, my = portrait ? 24 : 8, trunk = !portrait && L.bands > 1 ? 26 : 0, edges = labEdges(m, cx, L);
  const right = portrait && m.edges.some((e) => e.kind === "witness") ? 30 : 0;
  m.root.r = m.root.irPad = { x: L.x0 - mx - trunk, y: -my, w: L.w + 2 * mx + right + trunk, h: L.h + 2 * my };
  return { world: m.root.r, edges, decor: L.decor };
}
